
import React, { useMemo, useState } from 'react';
import { useResponsive } from '../hooks/useBreakpoint';
import Sidebar from './Sidebar';
import type { SidebarProps } from './Sidebar';
import MobileSidebar from './MobileSidebar';

export interface ResponsiveComponentWrapperProps {
  mobileComponent: React.ReactElement;
  desktopComponent: React.ReactElement;
}

// ResponsiveSidebar: switches between Sidebar and MobileSidebar
export const ResponsiveSidebar: React.FC<SidebarProps> = (props) => {
  const { isMobileOrTablet } = useResponsive();
  const [expanded, setExpanded] = useState(true);
  
  const handleToggle = () => {
    setExpanded(prev => !prev);
  };

  // Filter out quick-actions for desktop, keep them for mobile
  const filteredChildren = React.useMemo(() => {
    if (isMobileOrTablet) {
      return props.children; // Keep all children for mobile
    }
    
    // For desktop, filter out quick-actions
    if (React.isValidElement(props.children)) {
      return props.children;
    }
    
    return React.Children.toArray(props.children).filter((child) => {
      if (React.isValidElement(child) && (child.props as any)?.className?.includes('quick-actions')) {
        return false; // Remove quick-actions from desktop
      }
      return true;
    });
  }, [props.children, isMobileOrTablet]);
  
  return isMobileOrTablet ? 
    <MobileSidebar {...props} children={filteredChildren} /> : 
    <Sidebar {...props} expanded={expanded} onToggle={handleToggle} children={filteredChildren} />;
};

export const ResponsiveComponentWrapper: React.FC<ResponsiveComponentWrapperProps> = ({
  mobileComponent,
  desktopComponent
}) => {
  const { isMobileOrTablet } = useResponsive();
  
  const activeComponent = useMemo(() => {
    return isMobileOrTablet ? mobileComponent : desktopComponent;
  }, [isMobileOrTablet, mobileComponent, desktopComponent]);

  return <>{activeComponent}</>;
};

interface ResponsiveHeaderProps {
  headerProps: any;
}

export const ResponsiveHeader: React.FC<ResponsiveHeaderProps> = ({ 
  headerProps
}) => {
  const MobileHeaderComponent = () => (
    <div style={{ padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
      📱 Mobile Header: {headerProps.title} (User: {headerProps.userName})
    </div>
  );
  
  const DesktopHeaderComponent = () => (
    <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px' }}>
      💻 Desktop Header: {headerProps.title} - Welcome {headerProps.userName}
    </div>
  );

  return (
    <ResponsiveComponentWrapper
      mobileComponent={<MobileHeaderComponent />}
      desktopComponent={<DesktopHeaderComponent />}
    />
  );
};

interface ResponsiveRoomCardProps {
  roomCardProps: any;
}

export const ResponsiveRoomCard: React.FC<ResponsiveRoomCardProps> = ({ 
  roomCardProps
}) => {
  const MobileRoomCardComponent = () => (
    <div style={{ 
      padding: '10px', 
      backgroundColor: '#fff3e0', 
      borderRadius: '4px',
      border: '1px solid #ff9800'
    }}>
      <div>📱 Mobile Room Card</div>
      <div>Room: {roomCardProps.roomName}</div>
      <div>Players: {roomCardProps.playerCount}/{roomCardProps.maxPlayers}</div>
    </div>
  );
  
  const DesktopRoomCardComponent = () => (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#e8f5e8', 
      borderRadius: '12px',
      border: '2px solid #4caf50',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div>
        <div>💻 Desktop Room Card</div>
        <div><strong>{roomCardProps.roomName}</strong> ({roomCardProps.gameType})</div>
        <div>Players: {roomCardProps.playerCount}/{roomCardProps.maxPlayers}</div>
      </div>
      <button onClick={roomCardProps.onJoinRoom}>Join Room</button>
    </div>
  );

  return (
    <ResponsiveComponentWrapper
      mobileComponent={<MobileRoomCardComponent />}
      desktopComponent={<DesktopRoomCardComponent />}
    />
  );
};

export default ResponsiveComponentWrapper;
